import type { ProtoWallet } from '@ducat-unit/client-sdk';

import type { QuantaHeaders } from './auth';

export type QuantaIdentityRole = 'payment' | 'vault';
export interface QuantaIdentity {
  address: string;
  publicKey: string;
}
export interface QuantaIdentities {
  payment: QuantaIdentity;
  vault: QuantaIdentity | null;
}

const verifiedIdentities = new WeakMap<ProtoWallet, { revision: number; identities: QuantaIdentities }>();
const canonicalAddress = (address: string) => (/^(bc1|tb1|bcrt1)/i.test(address) ? address.toLowerCase() : address);
const canonicalKey = (identity: QuantaIdentity) =>
  (/^(bc1p|tb1p|bcrt1p)/i.test(identity.address) && identity.publicKey.length === 66
    ? identity.publicKey.slice(2)
    : identity.publicKey
  ).toLowerCase();

export function sameQuantaIdentity(left: QuantaIdentity, right: QuantaIdentity): boolean {
  return canonicalAddress(left.address) === canonicalAddress(right.address) && canonicalKey(left) === canonicalKey(right);
}

export function rememberQuantaIdentities(wallet: ProtoWallet, revision: number, identities: QuantaIdentities): void {
  verifiedIdentities.set(wallet, { revision, identities });
}

export function currentQuantaIdentities(wallet: ProtoWallet | null, revision: number): Partial<QuantaIdentities> {
  if (!wallet) return {};
  const cached = verifiedIdentities.get(wallet);
  if (cached?.revision === revision) return cached.identities;
  // Exact snapshot identities are safe before the first live lookup. Xverse's
  // real payment address may differ; its live lookup replaces this fallback.
  const payment = wallet.account?.funds;
  const vault = wallet.account?.vault;
  return {
    ...(payment?.address && payment.pubkey ? { payment: { address: payment.address, publicKey: payment.pubkey } } : {}),
    ...(vault?.address && vault.pubkey ? { vault: { address: vault.address, publicKey: vault.pubkey } } : {}),
  };
}

export function quantaHeadersBelongToWallet(
  headers: QuantaHeaders | undefined,
  wallet: ProtoWallet | null,
  revision: number,
  role?: QuantaIdentityRole,
): boolean {
  if (!headers?.['x-address'] || !headers['x-user-public-key']) return false;
  const current = currentQuantaIdentities(wallet, revision);
  const candidates = role ? [current[role]] : [current.payment, current.vault];
  return candidates.some(
    (identity) => identity && sameQuantaIdentity(identity, { address: headers['x-address'], publicKey: headers['x-user-public-key'] }),
  );
}
